import { MAX_TASK_REPEAT_INTERVAL, } from '../types/event-ops.js';
/**
 * A task carries its completion twice — the `isDone` tick and the `status`
 * badge — and every reader (the task page, the portal, the overview, the
 * dashboard) asks one or the other. These helpers are the one rule that keeps
 * them saying the same thing.
 */
/** Task dates are the office's calendar days, not the server's — Lambda runs in UTC. */
export const OFFICE_TIME_ZONE = 'America/New_York';
/** Done when either field says so — the reading rows written before the two were kept in step need. */
export function isChecklistItemDone(item) {
    return item.isDone || item.status === 'DONE';
}
/**
 * The `isDone`/`status` pair a write should store, given what it asked to
 * change and (for an update) what is stored now. A status wins over a tick
 * when both are sent; ticking a task marks it DONE, and unticking a DONE task
 * reopens it as TODO while leaving IN_PROGRESS/BLOCKED alone. Returns an empty
 * object when the write touches neither.
 */
export function resolveChecklistDone(patch, current) {
    if (patch.status !== undefined) {
        return { status: patch.status, isDone: patch.status === 'DONE' };
    }
    if (patch.isDone !== undefined) {
        if (patch.isDone)
            return { isDone: true, status: 'DONE' };
        const status = !current || current.status === 'DONE' ? 'TODO' : current.status;
        return { isDone: false, status };
    }
    return {};
}
/** The calendar day an instant falls on in the office's time zone, as a UTC-midnight date. */
export function officeCalendarDate(instant, timeZone = OFFICE_TIME_ZONE) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(instant);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    return new Date(Date.UTC(get('year'), get('month') - 1, get('day')));
}
/**
 * A template task's due date on a real event: the event's own calendar day
 * (in the office's time zone — an 8pm event is not tomorrow's) moved by the
 * template's offset. Stored as UTC midnight, as every date-only column is.
 */
export function dueDateFromOffset(eventStartAt, offsetDays) {
    const day = officeCalendarDate(eventStartAt);
    day.setUTCDate(day.getUTCDate() + offsetDays);
    return day;
}
/** Whether a string is a real calendar day written YYYY-MM-DD — not just that shape (2026-13-45 is not). */
export function isIsoDay(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    const d = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}
/** YYYY-MM-DD of a date-only value (stored at UTC midnight). */
export function dateOnlyKey(value) {
    return new Date(value).toISOString().slice(0, 10);
}
/**
 * YYYY-MM-DD of the viewer's own day — what "today" means for a date typed
 * into a form. `new Date().toISOString()` is the UTC day, which in New York
 * is already tomorrow from 8 PM.
 */
export function localDayKey(now = new Date()) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
/**
 * A date-only value (stored at UTC midnight) as the calendar day it names.
 * `new Date(d).toLocaleDateString()` reads it in the viewer's time zone, and
 * anywhere west of UTC that is the evening before — a bill due the 15th shown
 * as due the 14th. '' for no date.
 */
export function formatDateOnly(value, options = {}, locale = 'en-US') {
    if (!value)
        return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return '';
    return date.toLocaleDateString(locale, { ...options, timeZone: 'UTC' });
}
/** Whether a date-only value is before the viewer's today (`localDayKey`). */
export function isDateOnlyPast(value, todayKey = localDayKey()) {
    if (!value)
        return false;
    return dateOnlyKey(value) < todayKey;
}
/** The office's calendar day at an instant, `YYYY-MM-DD`. */
export function officeDayKey(at = new Date(), timeZone = OFFICE_TIME_ZONE) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(at);
}
/** YYYY-MM-DD of a calendar date `days` after `dayKey`. */
export function addDaysToKey(dayKey, days) {
    const d = new Date(`${dayKey}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}
/**
 * Which due bucket a task falls in for a viewer whose today is `todayKey`:
 * before today, today, the next seven days, later, or no date. The server
 * filters by the same boundaries, so a grouped list and its counts agree.
 */
export function taskDueBucket(dueDate, todayKey) {
    if (!dueDate)
        return 'none';
    const key = dateOnlyKey(dueDate);
    if (key < todayKey)
        return 'overdue';
    if (key === todayKey)
        return 'today';
    if (key < addDaysToKey(todayKey, 8))
        return 'week';
    return 'later';
}
/**
 * Whether somebody gets the daily task email. Their own choice when they have
 * made one; otherwise the office (ADMIN, PLANNER) does and a client does not —
 * a daily email to a client is something they ask for, not something they get.
 *
 * ⚠️ **Never a vendor**, whatever is stored: a vendor reads no tasks anywhere
 * in the app (the portal's task routes are a client's), so an email listing
 * them would be the one way round that.
 */
export function wantsDailyTaskEmail(systemRole, setting) {
    if (systemRole === 'VENDOR')
        return false;
    if (setting === true || setting === false)
        return setting;
    return systemRole === 'ADMIN' || systemRole === 'PLANNER';
}
/** Settings → App Settings: the office hour the daily task email goes out from. */
export const TASK_DIGEST_HOUR_SETTING = 'task_digest_hour';
/** Settings → App Settings: how many days after today the daily task email looks. */
export const TASK_DIGEST_DAYS_SETTING = 'task_digest_days_ahead';
export const DEFAULT_TASK_DIGEST_HOUR = 7;
export const DEFAULT_TASK_DIGEST_DAYS = 7;
export const MAX_TASK_DIGEST_DAYS = 30;
function wholeInRange(value, min, max, fallback) {
    const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
    return typeof n === 'number' && Number.isInteger(n) && n >= min && n <= max ? n : fallback;
}
/**
 * The daily task email's schedule from the two App Settings, as stored. A
 * value an administrator typed wrong — blank, 7.5, 25 — falls back to the
 * default rather than stopping the email: a morning email that silently
 * never comes is worse than one at the usual time.
 */
export function taskDigestSchedule(hour, daysAhead) {
    return {
        hour: wholeInRange(hour, 0, 23, DEFAULT_TASK_DIGEST_HOUR),
        daysAhead: wholeInRange(daysAhead, 0, MAX_TASK_DIGEST_DAYS, DEFAULT_TASK_DIGEST_DAYS),
    };
}
/** "7 AM", "12 PM", "3 PM" — an hour of the office's day as people say it. */
export function formatDigestHour(hour) {
    const h = hour % 12 === 0 ? 12 : hour % 12;
    return `${h} ${hour < 12 ? 'AM' : 'PM'}`;
}
/** "the next 7 days", "tomorrow", or "" when the email looks no further than today. */
export function describeDigestLookAhead(daysAhead) {
    if (daysAhead <= 0)
        return '';
    if (daysAhead === 1)
        return 'tomorrow';
    return `the next ${daysAhead} days`;
}
/** The day `count` months after `dayKey`, on the same date or the month's last day when it is shorter. */
function addMonthsToKey(dayKey, count) {
    const [y, m, d] = dayKey.split('-').map(Number);
    const first = new Date(Date.UTC(y, m - 1 + count, 1));
    const lastDay = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
    first.setUTCDate(Math.min(d, lastDay));
    return first.toISOString().slice(0, 10);
}
/** One repeat on from `dayKey`. */
export function stepRepeat(dayKey, unit, interval) {
    if (unit === 'MONTH')
        return addMonthsToKey(dayKey, interval);
    return addDaysToKey(dayKey, unit === 'WEEK' ? interval * 7 : interval);
}
/** Whole days from one YYYY-MM-DD to another. */
function daysBetween(fromKey, toKey) {
    return Math.round((Date.parse(`${toKey}T00:00:00Z`) - Date.parse(`${fromKey}T00:00:00Z`)) / 86_400_000);
}
/**
 * When a repeating task comes round next, once the one due `dueKey` is done.
 *
 * Steps on from the due date — not from the day it was done, so a weekly
 * Monday task stays on Mondays when it is ticked on a Wednesday — to the first
 * step that is not before today, so a task finished weeks late puts one task
 * on today or after, never a pile of overdue ones. It is always after
 * `dueKey`, and worked out directly rather than a step at a time, so however
 * old the due date, the answer is never left in the past. Months count from
 * the due date itself: the 31st every month is the last day of a shorter one.
 */
export function nextRepeatDue(dueKey, unit, interval, todayKey) {
    const step = Math.max(1, Math.min(MAX_TASK_REPEAT_INTERVAL, Math.trunc(interval) || 1));
    if (unit === 'MONTH') {
        const [dy, dm] = dueKey.split('-').map(Number);
        const [ty, tm] = todayKey.split('-').map(Number);
        const monthsAhead = (ty - dy) * 12 + (tm - dm);
        let steps = Math.max(1, Math.ceil(monthsAhead / step));
        let next = stepRepeat(dueKey, 'MONTH', steps * step);
        // Same month as today but an earlier day: one more.
        while (next < todayKey)
            next = stepRepeat(dueKey, 'MONTH', ++steps * step);
        return next;
    }
    const days = unit === 'WEEK' ? step * 7 : step;
    const behind = daysBetween(dueKey, todayKey);
    const steps = Math.max(1, Math.ceil(behind / days));
    return addDaysToKey(dueKey, steps * days);
}
const REPEAT_NOUNS = {
    DAY: ['day', 'days'],
    WEEK: ['week', 'weeks'],
    MONTH: ['month', 'months'],
};
/** "Every week", "Every 2 weeks" — or null for a task that does not repeat. */
export function describeRepeat(unit, interval) {
    if (!unit)
        return null;
    const n = interval && interval > 1 ? interval : 1;
    const [one, many] = REPEAT_NOUNS[unit];
    return n === 1 ? `Every ${one}` : `Every ${n} ${many}`;
}
//# sourceMappingURL=checklist.js.map